const DEFAULT_STRIP_HEIGHT_PCT = 0.32;
const DEFAULT_BOTTOM_GAP = 24;

/** Canvas cannot resolve CSS variables — keep in sync with :root --brand-primary */
const BRAND_PRIMARY_HEX = '#6a5cb8';
const BRAND_PRIMARY_STROKE_RGBA = 'rgba(106, 92, 184, 0.95)';

// Match SyncedTelemetryChart: same metric order and colors
const CHART_METRIC_COLORS = {
  speed: BRAND_PRIMARY_HEX,
  frontBrake: '#4CAF50',
  rearBrake: '#2196F3',
  heartRate: '#E91E63',
  power: '#FFC107',
  elevation: '#9C27B0',
  distance: '#00BCD4',
  fork: '#8BC34A',
  shock: '#CDDC39',
  cadence: '#9575cd',
};
const CHART_EXTRA_COLORS = ['#673AB7', '#3F51B5', '#009688', '#4CAF50', '#8BC34A', '#FFEB3B'];
const CHART_EXCLUDED_KEYS = new Set(['time', 'lat', 'lon', 'position_lat', 'position_long']);
const CHART_HANDLED_KEYS = new Set(['speed', 'front_brake', 'rear_brake', 'fork', 'fork_normalized', 'shock', 'shock_normalized', 'heartRate', 'heart_rate', 'power', 'cadence', 'elevation', 'distance', 'gps_speed']);

function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getValidNumber(v) {
  if (v == null) return null;
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

/**
 * Build graph series from telemetry in the same order and colors as SyncedTelemetryChart.
 * @param {Array<Object>} data - Telemetry rows
 * @param {{ [metricKey: string]: boolean }} [visibleMetrics] - Optional; if provided, only include series where visibleMetrics[key] !== false
 * @returns {{ getTime: (d) => number, seriesList: Array<{ key: string, color: string, values: Array<{t: number, v: number}> }>, tMin: number, tMax: number, timeRange: number }}
 */
function buildChartSeriesFromTelemetry(data, visibleMetrics = {}) {
  const getTime = (d) => getValidNumber(d.time ?? d.timestamp ?? d.elapsed ?? d.Time) ?? 0;
  const seriesList = [];
  if (!data || data.length === 0) {
    const times = [];
    return { getTime, seriesList, tMin: 0, tMax: 0, timeRange: 0.001 };
  }

  const toValues = (arr) => arr.map((v, i) => ({ t: getTime(data[i]), v: v == null || Number.isNaN(v) ? NaN : v })).filter((p) => !Number.isNaN(p.v));
  const pushSeries = (metricKey, values, color) => {
    if (visibleMetrics[metricKey] === false) return;
    if (values.length < 2) return;
    if (values.length === 1) values.push({ ...values[0], t: values[0].t + 0.001 });
    seriesList.push({ key: metricKey, color, values });
  };

  const rawSpeed = data.map((d) => getValidNumber(d.speed) ?? 0);
  const maxSpeed = Math.max(...rawSpeed, 1);
  const speedData = rawSpeed.map((s) => (s / maxSpeed) * 1000);
  pushSeries('speed', toValues(speedData), CHART_METRIC_COLORS.speed);

  const frontBrakeData = data.map((d) => getValidNumber(d.front_brake) ?? 0);
  if (frontBrakeData.some((v) => v > 0)) pushSeries('frontBrake', toValues(frontBrakeData), CHART_METRIC_COLORS.frontBrake);

  const rearBrakeData = data.map((d) => getValidNumber(d.rear_brake) ?? 0);
  if (rearBrakeData.some((v) => v > 0)) pushSeries('rearBrake', toValues(rearBrakeData), CHART_METRIC_COLORS.rearBrake);

  const forkData = data.map((d) => getValidNumber(d.fork_normalized) ?? getValidNumber(d.fork) ?? null);
  if (forkData.some((v) => v != null && v > 0)) pushSeries('fork', toValues(forkData), CHART_METRIC_COLORS.fork);

  const shockData = data.map((d) => getValidNumber(d.shock_normalized) ?? getValidNumber(d.shock) ?? null);
  if (shockData.some((v) => v != null && v > 0)) pushSeries('shock', toValues(shockData), CHART_METRIC_COLORS.shock);

  const heartRateData = data.map((d) => getValidNumber(d.heartRate) ?? getValidNumber(d.heart_rate) ?? 0);
  if (heartRateData.some((v) => v > 0)) pushSeries('heartRate', toValues(heartRateData), CHART_METRIC_COLORS.heartRate);

  const powerData = data.map((d) => (getValidNumber(d.power) ?? 0) * 20);
  if (powerData.some((v) => v > 0)) pushSeries('power', toValues(powerData), CHART_METRIC_COLORS.power);

  const cadenceData = data.map((d) => getValidNumber(d.cadence) ?? 0);
  if (cadenceData.some((v) => v > 0)) pushSeries('cadence', toValues(cadenceData), CHART_METRIC_COLORS.cadence);

  const elevationData = data.map((d) => (typeof d.elevation === 'number' ? d.elevation : null));
  if (elevationData.some((v) => v != null && Number.isFinite(v))) pushSeries('elevation', toValues(elevationData), CHART_METRIC_COLORS.elevation);

  let distanceKmData = null;
  const distanceValues = data.map((d) => getValidNumber(d.distance)).filter((v) => v != null);
  if (distanceValues.length >= 2) {
    const maxD = Math.max(...distanceValues);
    const minD = Math.min(...distanceValues);
    const isMeters = maxD > 1000;
    const minVal = isMeters ? minD / 1000 : minD;
    distanceKmData = data.map((d) => {
      const v = getValidNumber(d.distance);
      if (v == null) return null;
      const km = isMeters ? v / 1000 : v;
      return Math.max(0, km - minVal);
    });
  } else if (data.some((d) => getValidNumber(d.lat) != null && getValidNumber(d.lon) != null)) {
    let total = 0;
    let prev = null;
    distanceKmData = data.map((d) => {
      const lat = getValidNumber(d.lat);
      const lon = getValidNumber(d.lon);
      if (lat == null || lon == null) return null;
      if (prev) total += haversineKm(prev.lat, prev.lon, lat, lon);
      prev = { lat, lon };
      return total;
    });
  }
  if (distanceKmData && distanceKmData.some((v) => v != null && Number.isFinite(v))) pushSeries('distance', toValues(distanceKmData), CHART_METRIC_COLORS.distance);

  const firstRow = data[0] || {};
  const handledKeys = new Set(CHART_HANDLED_KEYS);
  let extraIdx = 0;
  for (const key of Object.keys(firstRow)) {
    if (CHART_EXCLUDED_KEYS.has(key) || handledKeys.has(key)) continue;
    const values = data.map((d) => getValidNumber(d[key]));
    if (!values.some((v) => v != null)) continue;
    handledKeys.add(key);
    const metricKey = 'extra_' + key;
    if (visibleMetrics[metricKey] === false) continue;
    const withTime = toValues(values);
    if (withTime.length < 2) continue;
    if (withTime.length === 1) withTime.push({ ...withTime[0], t: withTime[0].t + 0.001 });
    seriesList.push({ key: metricKey, color: CHART_EXTRA_COLORS[extraIdx % CHART_EXTRA_COLORS.length], values: withTime });
    extraIdx += 1;
  }

  const times = data.map(getTime).filter((n) => Number.isFinite(n));
  const tMin = times.length ? Math.min(...times) : 0;
  const tMax = times.length ? Math.max(...times) : 0;
  const timeRange = Math.max(tMax - tMin, 0.001);
  return { getTime, seriesList, tMin, tMax, timeRange };
}

/**
 * Resolve layout dimensions from templateId or custom layoutConfig.
 * Supports legacy (graphHeightPct + gps) and new format (graph + gps with xPct, yPct, widthPct, heightPct each).
 * @param {string} [templateId]
 * @param {{ graphHeightPct?: number, graph?: { xPct: number, yPct: number, widthPct: number, heightPct: number }, gps?: { xPct: number, yPct: number, widthPct: number, heightPct: number } }} [layoutConfig]
 * @param {number} width
 * @param {number} height
 * @returns {{ hasGraph: boolean, hasMap: boolean, graphHeight: number, videoH: number, graphX: number, graphY: number, graphBoxW: number, graphBoxH: number, gpsX: number, gpsY: number, gpsBoxW: number, gpsBoxH: number }}
 */
function resolveLayout(templateId, layoutConfig, width, height) {
  const gpsBox = (gps) => ({
    gpsX: Math.floor(width * (gps.xPct ?? 0.76)),
    gpsY: Math.floor(height * (gps.yPct ?? 0.03)),
    gpsBoxW: Math.floor(width * (gps.widthPct ?? 0.24)),
    gpsBoxH: Math.floor(height * (gps.heightPct ?? 0.22)),
  });
  const graphBox = (g) => ({
    graphX: Math.floor(width * (g.xPct ?? 0)),
    graphY: Math.floor(height * (g.yPct ?? 0.68)),
    graphBoxW: Math.floor(width * (g.widthPct ?? 1)),
    graphBoxH: Math.floor(height * (g.heightPct ?? 0.32)),
  });

  if (layoutConfig) {
    const hasMap = !!layoutConfig.gps;
    const hasGraphBox = !!layoutConfig.graph;
    const hasGraphStrip = !hasGraphBox && (layoutConfig.graphHeightPct ?? 0) > 0;
    const hasGraph = hasGraphBox || hasGraphStrip;

    let videoH, graphHeight, graphX, graphY, graphBoxW, graphBoxH;
    if (hasGraphBox) {
      const g = graphBox(layoutConfig.graph);
      graphX = g.graphX;
      graphY = g.graphY;
      graphBoxW = g.graphBoxW;
      graphBoxH = g.graphBoxH;
      videoH = height;
      graphHeight = 0;
    } else if (hasGraphStrip) {
      graphHeight = Math.floor(height * (layoutConfig.graphHeightPct ?? DEFAULT_STRIP_HEIGHT_PCT)) + DEFAULT_BOTTOM_GAP;
      videoH = height - graphHeight;
      graphX = 0;
      graphY = videoH;
      graphBoxW = width;
      graphBoxH = graphHeight;
    } else {
      graphHeight = 0;
      videoH = height;
      graphX = graphY = graphBoxW = graphBoxH = 0;
    }

    const gps = hasMap ? gpsBox(layoutConfig.gps) : { gpsX: 0, gpsY: 0, gpsBoxW: 0, gpsBoxH: 0 };
    return { hasGraph, hasMap, graphHeight, videoH, graphX, graphY, graphBoxW, graphBoxH, ...gps };
  }

  const hasGraph = templateId === 'video_graph_gps' || templateId === 'video_graph_only';
  const hasMap = templateId === 'video_graph_gps' || templateId === 'video_gps_only';
  const graphHeight = hasGraph ? Math.floor(height * DEFAULT_STRIP_HEIGHT_PCT) + DEFAULT_BOTTOM_GAP : 0;
  const videoH = height - graphHeight;
  const gpsBoxW = hasMap ? Math.min(Math.floor(width * 0.24), 280) : 0;
  const gpsBoxH = hasMap ? Math.min(Math.floor(height * 0.22), 180) : 0;
  const gpsX = width - gpsBoxW - 20;
  const gpsY = 20;
  const graphX = 0;
  const graphY = videoH;
  const graphBoxW = width;
  const graphBoxH = graphHeight;
  return { hasGraph, hasMap, graphHeight, videoH, graphX, graphY, graphBoxW, graphBoxH, gpsX, gpsY, gpsBoxW, gpsBoxH };
}

/**
 * Generate a single preview frame for an export template at a given video time.
 * @param {Object} opts
 * @param {string} opts.videoUrl
 * @param {Array} opts.telemetryData
 * @param {string} [opts.templateId]
 * @param {{ graphHeightPct?: number, gps?: { xPct, yPct, widthPct, heightPct } }} [opts.layoutConfig]
 * @param {number} [opts.timeSec=0]
 * @param {number} [opts.width=320]
 * @param {number} [opts.height=180]
 * @returns {Promise<string>} Data URL of the preview image
 */
export function getExportPreviewFrame({ videoUrl, telemetryData, templateId, layoutConfig, timeSec = 0, dataOffset = 0, width = 320, height = 180, visibleMetrics: visibleMetricsOpt }) {
  const data = Array.isArray(telemetryData) ? telemetryData : [];
  const layout = resolveLayout(templateId, layoutConfig, width, height);
  const { hasGraph, hasMap, videoH, graphX, graphY, graphBoxW, graphBoxH, gpsX, gpsY, gpsBoxW: gpsW, gpsBoxH: gpsH } = layout;
  const hasGps = hasMap && data.some((d) => (d.lat != null && d.lon != null) || (d.position_lat != null && d.position_long != null));
  const visibleMetrics = visibleMetricsOpt ?? {};

  const toNum = (v) => {
    if (v == null) return NaN;
    const n = typeof v === 'string' ? Number(v) : v;
    return typeof n === 'number' && Number.isFinite(n) ? n : NaN;
  };
  const SEMICIRCLE_TO_DEG = 180 / (1 << 31);
  const toDeg = (v) => { const n = toNum(v); if (Number.isNaN(n)) return n; if (n > 90 || n < -90) return n * SEMICIRCLE_TO_DEG; return n; };
  const toDegLon = (v) => { const n = toNum(v); if (Number.isNaN(n)) return n; if (n > 180 || n < -180) return n * SEMICIRCLE_TO_DEG; return n; };
  const chartBuilt = hasGraph && data.length > 0 ? buildChartSeriesFromTelemetry(data, visibleMetrics) : { getTime: (d) => toNum(d.time ?? d.timestamp ?? d.elapsed ?? d.Time) ?? 0, seriesList: [], tMin: 0, tMax: 0, timeRange: 0.001 };
  const { getTime, seriesList, tMin, tMax, timeRange } = chartBuilt;
  const gpsRows = hasGps ? data.filter((d) => (d.lat != null && d.lon != null) || (d.position_lat != null && d.position_long != null)) : [];
  const latLon = [];
  const gpsTimes = [];
  for (const d of gpsRows) {
    const lat = toDeg(d.lat ?? d.position_lat);
    const lon = toDegLon(d.lon ?? d.position_long);
    const t = getTime(d);
    if (!Number.isNaN(lat) && !Number.isNaN(lon)) { latLon.push({ lat, lon }); gpsTimes.push(Number.isNaN(t) ? 0 : t); }
  }
  let latMin = 0, latMax = 0, lonMin = 0, lonMax = 0;
  if (latLon.length > 0) {
    latMin = Math.min(...latLon.map((p) => p.lat)); latMax = Math.max(...latLon.map((p) => p.lat));
    lonMin = Math.min(...latLon.map((p) => p.lon)); lonMax = Math.max(...latLon.map((p) => p.lon));
    if (latMax === latMin) latMax = latMin + 0.0001; if (lonMax === lonMin) lonMax = lonMin + 0.0001;
  }

  const isYouTube = typeof videoUrl === 'string' && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'));
  if (isYouTube) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return Promise.reject(new Error('Canvas not supported'));
    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#666';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('YouTube – preview not available (export works on server)', width / 2, height / 2);
    if (hasGps && latLon.length > 0) {
      const pad = 4;
      const gL = gpsX + pad; const gT = gpsY + pad; const gW = Math.max(0, gpsW - pad * 2); const gH = Math.max(0, gpsH - pad * 2);
      const lonSpan = lonMax - lonMin; const latSpan = latMax - latMin;
      const toX = (lon) => gL + (lonSpan > 0 ? (lon - lonMin) / lonSpan : 0) * gW;
      const toY = (lat) => gT + gH - (latSpan > 0 ? (lat - latMin) / latSpan : 0) * gH;
      ctx.strokeStyle = BRAND_PRIMARY_STROKE_RGBA; ctx.lineWidth = 2; ctx.beginPath();
      for (let i = 0; i < latLon.length; i++) { const x = toX(latLon[i].lon); const y = toY(latLon[i].lat); if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); }
      ctx.stroke();
      const targetTime = timeSec + dataOffset;
      let idx = 0; for (let i = 0; i < gpsTimes.length; i++) { if (gpsTimes[i] != null && gpsTimes[i] <= targetTime) idx = i; }
      ctx.fillStyle = '#fff'; ctx.strokeStyle = BRAND_PRIMARY_HEX; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(toX(latLon[idx].lon), toY(latLon[idx].lat), 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    }
    if (hasGraph && graphBoxW > 0 && graphBoxH > 0 && seriesList.length > 0) {
      const gx0 = graphX + 8; const gx1 = graphX + graphBoxW - 8; const gy0 = graphY + 6; const gy1 = graphY + graphBoxH - 6;
      const targetTime = timeSec + dataOffset;
      const graphWidth = gx1 - gx0; const centerX = (gx0 + gx1) / 2;
      const defaultZoom = (templateId === 'video_graph_gps' || templateId === 'video_graph_only') ? 8 : 1;
      const zoomFactor = Math.max(1, layoutConfig?.graph?.zoom ?? layoutConfig?.graphZoom ?? defaultZoom);
      const halfWindow = timeRange / (2 * zoomFactor);
      const pixelsPerSecond = graphWidth / (2 * halfWindow);
      const xForT = (pt) => Math.max(gx0, Math.min(gx1, centerX + (pt - targetTime) * pixelsPerSecond));
      const nowX = centerX;
      for (const series of seriesList) {
        const vals = series.values;
        const vMin = Math.min(...vals.map((p) => p.v)); let vMax = Math.max(...vals.map((p) => p.v)); if (vMax === vMin) vMax = vMin + 1;
        const vRange = vMax - vMin; const yForV = (v) => gy1 - ((v - vMin) / vRange) * (gy1 - gy0);
        ctx.strokeStyle = series.color; ctx.lineWidth = 2; ctx.beginPath();
        for (let i = 0; i < vals.length; i++) { const x = xForT(vals[i].t); const y = Math.max(gy0, Math.min(gy1, yForV(vals[i].v))); if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); }
        ctx.stroke();
      }
      ctx.strokeStyle = 'rgba(255,255,255,0.85)'; ctx.lineWidth = 1; ctx.setLineDash([2, 2]);
      ctx.beginPath(); ctx.moveTo(nowX, gy0); ctx.lineTo(nowX, gy1); ctx.stroke(); ctx.setLineDash([]);
    }
    return Promise.resolve(canvas.toDataURL('image/jpeg', 0.85));
  }

  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    video.src = videoUrl;
    video.onerror = () => reject(new Error('Video failed to load'));
    video.onloadedmetadata = () => {
      video.currentTime = Math.max(0, Math.min(timeSec, video.duration || 0));
    };
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas not supported')); return; }
      const t = Math.max(0, video.currentTime);
      const targetTime = t + dataOffset;
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, width, height);
      const vw = video.videoWidth || width;
      const vh = video.videoHeight || height;
      const rectAspect = width / height;
      const videoAspect = vw / vh;
      let sx = 0, sy = 0, sw = vw, sh = vh;
      if (videoAspect > rectAspect) { sh = vw / rectAspect; sy = (vh - sh) / 2; } else { sw = vh * rectAspect; sx = (vw - sw) / 2; }
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, width, height);
      if (hasGps && latLon.length > 0) {
        const pad = 4;
        const gL = gpsX + pad; const gT = gpsY + pad; const gW = Math.max(0, gpsW - pad * 2); const gH = Math.max(0, gpsH - pad * 2);
        const lonSpan = lonMax - lonMin; const latSpan = latMax - latMin;
        const toX = (lon) => gL + (lonSpan > 0 ? (lon - lonMin) / lonSpan : 0) * gW;
        const toY = (lat) => gT + gH - (latSpan > 0 ? (lat - latMin) / latSpan : 0) * gH;
        ctx.strokeStyle = BRAND_PRIMARY_STROKE_RGBA; ctx.lineWidth = 2; ctx.beginPath();
        for (let i = 0; i < latLon.length; i++) { const x = toX(latLon[i].lon); const y = toY(latLon[i].lat); if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); }
        ctx.stroke();
        let idx = 0; for (let i = 0; i < gpsTimes.length; i++) { if (gpsTimes[i] != null && gpsTimes[i] <= targetTime) idx = i; }
        ctx.fillStyle = '#fff'; ctx.strokeStyle = BRAND_PRIMARY_HEX; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(toX(latLon[idx].lon), toY(latLon[idx].lat), 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      }
      if (hasGraph && graphBoxW > 0 && graphBoxH > 0) {
        const gx0 = graphX + 8; const gx1 = graphX + graphBoxW - 8; const gy0 = graphY + 6; const gy1 = graphY + graphBoxH - 6;
        const graphWidth = gx1 - gx0;
        const centerX = (gx0 + gx1) / 2;
        const defaultZoom = (templateId === 'video_graph_gps' || templateId === 'video_graph_only') ? 8 : 1;
        const zoomFactor = Math.max(1, layoutConfig?.graph?.zoom ?? layoutConfig?.graphZoom ?? defaultZoom);
        const halfWindow = timeRange / (2 * zoomFactor);
        const pixelsPerSecond = graphWidth / (2 * halfWindow);
        const xForT = (pt) => Math.max(gx0, Math.min(gx1, centerX + (pt - targetTime) * pixelsPerSecond));
        const nowX = centerX;
        for (const series of seriesList) {
          const vals = series.values;
          const vMin = Math.min(...vals.map((p) => p.v)); let vMax = Math.max(...vals.map((p) => p.v)); if (vMax === vMin) vMax = vMin + 1;
          const vRange = vMax - vMin; const yForV = (v) => gy1 - ((v - vMin) / vRange) * (gy1 - gy0);
          ctx.strokeStyle = series.color; ctx.lineWidth = 2; ctx.beginPath();
          for (let i = 0; i < vals.length; i++) { const x = xForT(vals[i].t); const y = Math.max(gy0, Math.min(gy1, yForV(vals[i].v))); if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); }
          ctx.stroke();
        }
        ctx.strokeStyle = 'rgba(255,255,255,0.85)'; ctx.lineWidth = 1; ctx.setLineDash([2, 2]);
        ctx.beginPath(); ctx.moveTo(nowX, gy0); ctx.lineTo(nowX, gy1); ctx.stroke(); ctx.setLineDash([]);
      }
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    video.load();
  });
}

/**
 * Client-side export: record video + overlays (graph, optional map) to a blob using canvas and MediaRecorder.
 * Used when there is no server-usable video URL (e.g. creation mode or local file only). Does not support YouTube (iframe cannot be drawn to canvas).
 * @param {Object} opts
 * @param {string} opts.videoUrl - Direct or blob URL (not YouTube)
 * @param {Array<{ time?: number, speed?: number, power?: number, lat?: number, lon?: number, [key: string]: any }>} opts.telemetryData
 * @param {string} opts.templateId - e.g. 'video_graph_gps', 'video_graph_only', 'video_gps_only'
 * @param {{ graphHeightPct?: number, gps?: { xPct, yPct, widthPct, heightPct } }} [opts.layoutConfig] - Custom layout (overrides templateId layout)
 * @param {number} [opts.width=1280]
 * @param {number} [opts.height=720]
 * @param {(msg: string | { message: string, progress?: number, currentTime?: number, duration?: number, frameIndex?: number, estimatedTimeRemaining?: number }) => void} [opts.onProgress]
 * @param {number} [opts.maxDurationSeconds] - If set, stop recording after this many seconds (e.g. 1 for testing).
 * @param {number} [opts.startTimeSeconds] - Start recording at this video time (seconds).
 * @param {number} [opts.endTimeSeconds] - Stop recording at this video time (seconds).
 * @param {number} [opts.dataOffset=0] - Sync offset: dataTime = videoTime + dataOffset (seconds).
 * @returns {Promise<Blob>} - Recorded video blob (webm)
 */
export function recordVideoWithOverlays({ videoUrl, telemetryData, templateId, layoutConfig, width = 1280, height = 720, onProgress, maxDurationSeconds, startTimeSeconds, endTimeSeconds, dataOffset = 0, visibleMetrics: visibleMetricsOpt }) {
  const report = (msg) => { onProgress && onProgress(msg); };
  const reportProgress = (obj) => { onProgress && onProgress(obj); };
  const visibleMetrics = visibleMetricsOpt ?? {};

  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    video.src = videoUrl;

  const layout = resolveLayout(templateId, layoutConfig, width, height);
  const { hasGraph, hasMap, videoH, graphX, graphY, graphBoxW, graphBoxH, gpsX, gpsY, gpsBoxW: gpsW, gpsBoxH: gpsH } = layout;
    const data = Array.isArray(telemetryData) ? telemetryData : [];
    const hasGps = hasMap && data.some((d) => (d.lat != null && d.lon != null) || (d.position_lat != null && d.position_long != null));

    video.onerror = () => reject(new Error('Video failed to load'));
    video.preload = 'auto';
    video.onloadedmetadata = () => {
      const start = startTimeSeconds != null ? Math.max(0, startTimeSeconds) : 0;
      video.currentTime = start;
      let done = false;
      const startPlay = () => {
        if (done) return;
        done = true;
        video.removeEventListener('seeked', onSeeked);
        clearTimeout(seekTimeout);
        video.play().catch(reject);
      };
      const onSeeked = startPlay;
      video.addEventListener('seeked', onSeeked);
      const seekTimeout = setTimeout(startPlay, 3000);
    };

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas not supported'));
      return;
    }

    const toNum = (v) => {
      if (v == null) return NaN;
      const n = typeof v === 'string' ? Number(v) : v;
      return typeof n === 'number' && Number.isFinite(n) ? n : NaN;
    };
    const getTimeDefault = (d) => toNum(d.time ?? d.timestamp ?? d.elapsed ?? d.Time) ?? 0;
    const SEMICIRCLE_TO_DEG = 180 / (1 << 31);
    const toDeg = (v) => {
      const n = toNum(v);
      if (Number.isNaN(n)) return n;
      if (n > 90 || n < -90) return n * SEMICIRCLE_TO_DEG;
      return n;
    };
    const toDegLon = (v) => {
      const n = toNum(v);
      if (Number.isNaN(n)) return n;
      if (n > 180 || n < -180) return n * SEMICIRCLE_TO_DEG;
      return n;
    };
    const chartBuilt = hasGraph && data.length > 0 ? buildChartSeriesFromTelemetry(data, visibleMetrics) : { getTime: getTimeDefault, seriesList: [], tMin: 0, tMax: 0, timeRange: 0.001 };
    const { getTime, seriesList, tMin, tMax, timeRange } = chartBuilt;

    const gpsRows = hasGps
      ? data.filter((d) => (d.lat != null && d.lon != null) || (d.position_lat != null && d.position_long != null))
      : [];
    const latLon = [];
    const gpsTimes = [];
    for (const d of gpsRows) {
      const lat = toDeg(d.lat ?? d.position_lat);
      const lon = toDegLon(d.lon ?? d.position_long);
      const t = getTime(d);
      if (!Number.isNaN(lat) && !Number.isNaN(lon)) {
        latLon.push({ lat, lon });
        gpsTimes.push(Number.isNaN(t) ? 0 : t);
      }
    }
    let latMin = 0, latMax = 0, lonMin = 0, lonMax = 0;
    if (latLon.length > 0) {
      latMin = Math.min(...latLon.map((p) => p.lat));
      latMax = Math.max(...latLon.map((p) => p.lat));
      lonMin = Math.min(...latLon.map((p) => p.lon));
      lonMax = Math.max(...latLon.map((p) => p.lon));
      if (latMax === latMin) latMax = latMin + 0.0001;
      if (lonMax === lonMin) lonMax = lonMin + 0.0001;
    }

    function drawFrame() {
      const t = Math.max(0, video.currentTime);
      const targetTime = t + dataOffset;

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, width, height);

      const vw = video.videoWidth || width;
      const vh = video.videoHeight || height;
      const rectAspect = width / height;
      const videoAspect = vw / vh;
      let sx = 0, sy = 0, sw = vw, sh = vh;
      if (videoAspect > rectAspect) {
        sh = vw / rectAspect;
        sy = (vh - sh) / 2;
      } else {
        sw = vh * rectAspect;
        sx = (vw - sw) / 2;
      }
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, width, height);

      if (hasGps && latLon.length > 0) {
        const pad = 8;
        const gL = gpsX + pad;
        const gT = gpsY + pad;
        const gW = gpsW - pad * 2;
        const gH = gpsH - pad * 2;
        const lonSpan = lonMax - lonMin;
        const latSpan = latMax - latMin;
        const toX = (lon) => gL + (lonSpan > 0 ? (lon - lonMin) / lonSpan : 0) * gW;
        const toY = (lat) => gT + gH - (latSpan > 0 ? (lat - latMin) / latSpan : 0) * gH;

        ctx.strokeStyle = BRAND_PRIMARY_STROKE_RGBA;
        ctx.lineWidth = 3;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.beginPath();
        for (let i = 0; i < latLon.length; i++) {
          const x = toX(latLon[i].lon);
          const y = toY(latLon[i].lat);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();

        let idx = 0;
        for (let i = 0; i < gpsTimes.length; i++) {
          if (gpsTimes[i] != null && gpsTimes[i] <= targetTime) idx = i;
        }
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = BRAND_PRIMARY_HEX;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(toX(latLon[idx].lon), toY(latLon[idx].lat), 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }

      if (hasGraph && graphBoxW > 0 && graphBoxH > 0) {
        const gx0 = graphX + 16;
        const gx1 = graphX + graphBoxW - 16;
        const gy0 = graphY + 12;
        const gy1 = graphY + graphBoxH - 12;

        const graphWidth = gx1 - gx0;
        const centerX = (gx0 + gx1) / 2;
        const defaultZoom = (templateId === 'video_graph_gps' || templateId === 'video_graph_only') ? 8 : 1;
        const zoomFactor = Math.max(1, layoutConfig?.graph?.zoom ?? layoutConfig?.graphZoom ?? defaultZoom);
        const halfWindow = timeRange / (2 * zoomFactor);
        const pixelsPerSecond = graphWidth / (2 * halfWindow);
        const xForT = (pt) => Math.max(gx0, Math.min(gx1, centerX + (pt - targetTime) * pixelsPerSecond));
        const nowX = centerX;

        for (const series of seriesList) {
          const vals = series.values;
          const vMin = Math.min(...vals.map((p) => p.v));
          let vMax = Math.max(...vals.map((p) => p.v));
          if (vMax === vMin) vMax = vMin + 1;
          const vRange = vMax - vMin;
          const yForV = (v) => gy1 - ((v - vMin) / vRange) * (gy1 - gy0);

          ctx.strokeStyle = series.color;
          ctx.lineWidth = 3;
          ctx.lineJoin = 'round';
          ctx.lineCap = 'round';
          ctx.beginPath();
          for (let i = 0; i < vals.length; i++) {
            const x = xForT(vals[i].t);
            const y = Math.max(gy0, Math.min(gy1, yForV(vals[i].v)));
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.stroke();
        }

        ctx.strokeStyle = 'rgba(255,255,255,0.85)';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(nowX, gy0);
        ctx.lineTo(nowX, gy1);
        ctx.stroke();
        ctx.setLineDash([]);

        if (seriesList.length === 0 && data.length > 0) {
          ctx.strokeStyle = 'rgba(106, 92, 184, 0.5)';
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.moveTo(gx0, (gy0 + gy1) / 2);
          ctx.lineTo(gx1, (gy0 + gy1) / 2);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }
    }

    let stream = null;
    let recorder = null;
    const chunks = [];
    let frameIndex = 0;
    let lastProgressTime = 0;
    const PROGRESS_INTERVAL_MS = 200;

    video.onplay = () => {
      report('Recording…');
      stream = canvas.captureStream(30);
      const options = { mimeType: 'video/webm;codecs=vp9', videoBitsPerSecond: 2500000 };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm';
      }
      recorder = new MediaRecorder(stream, options);
      recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType });
        resolve(blob);
      };
      const startRecording = () => {
        if (recorder && recorder.state !== 'recording') recorder.start(200);
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          startRecording();
        });
      });
    };

    const getEffectiveDuration = () => {
      const d = Number.isFinite(video.duration) ? video.duration : 0;
      const start = startTimeSeconds != null ? Math.max(0, startTimeSeconds) : 0;
      const end = endTimeSeconds != null && endTimeSeconds > 0 ? Math.min(d, endTimeSeconds) : d;
      let effective = end - start;
      if (maxDurationSeconds != null && maxDurationSeconds > 0) effective = Math.min(effective, maxDurationSeconds);
      return { start, end: start + effective, duration: effective, fullDuration: d };
    };

    video.onended = () => {
      if (recorder && recorder.state === 'recording') {
        const { duration } = getEffectiveDuration();
        reportProgress({ message: 'Recording…', progress: 100, currentTime: duration, duration, frameIndex, estimatedTimeRemaining: 0 });
        recorder.stop();
      }
    };

    function tick() {
      if (video.paused || video.ended) return;
      const t = video.currentTime;
      const { start, end, duration: effectiveDuration } = getEffectiveDuration();
      frameIndex += 1;

      const shouldStop = (endTimeSeconds != null && t >= endTimeSeconds) || (maxDurationSeconds != null && maxDurationSeconds > 0 && t >= start + maxDurationSeconds);
      if (shouldStop && recorder && recorder.state === 'recording') {
        video.pause();
        reportProgress({ message: 'Recording…', progress: 100, currentTime: effectiveDuration, duration: effectiveDuration, frameIndex, estimatedTimeRemaining: 0 });
        recorder.stop();
        return;
      }

      const now = Date.now();
      if (now - lastProgressTime >= PROGRESS_INTERVAL_MS && effectiveDuration > 0) {
        lastProgressTime = now;
        const elapsed = Math.max(0, t - start);
        const progress = Math.min(100, (elapsed / effectiveDuration) * 100);
        const estimatedTimeRemaining = Math.max(0, effectiveDuration - elapsed);
        reportProgress({
          message: 'Recording…',
          progress,
          currentTime: elapsed,
          duration: effectiveDuration,
          frameIndex,
          estimatedTimeRemaining,
        });
      }

      drawFrame();
      requestAnimationFrame(tick);
    }
    video.onplaying = tick;

    video.load();
    report('Loading video…');
  });
}
